import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, BarChart3 } from "lucide-react";
import { Link } from "../components/LocaleLink";
import { useLang } from "../context/LanguageContext";
import { submitLead, optimizeImg } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { track, trackLead } from "../lib/analytics";
import { ChipGroup } from "../components/ChipGroup";
import { overline, inputClass, labelClass, StepHeading, BriefRow } from "../components/FlowBits";

const HERO = "https://images.unsplash.com/photo-1763114298920-eaac2534e739";
const labels = (list, ids) => list.filter((o) => ids.includes(o.id)).map((o) => o.label);

const GLANCE = [
  { k: "yield", v: "4.9%" },
  { k: "growth", v: "+3.6%" },
  { k: "occ", v: "71–88%" },
  { k: "adr", v: "€180–245" },
  { k: "median", v: "€520K" },
  { k: "notaire", v: "7–8%" },
];

export default function InvestorFlow() {
  const { t, lang } = useLang();
  const v = (k) => t(`investPage.${k}`);
  const dests = v("destinations");
  const budgets = v("budgets");
  const objectives = v("objectives");
  const horizons = v("horizons");
  const involvements = v("involvements");
  const L = v("labels");
  const G = v("glance");

  const [destIds, setDestIds] = useState([]);
  const [budget, setBudget] = useState("");
  const [objIds, setObjIds] = useState([]);
  const [horizon, setHorizon] = useState("");
  const [involvement, setInvolvement] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", note: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const destLabels = labels(dests, destIds);
  const budgetLabel = labels(budgets, [budget])[0] || "";
  const objLabels = labels(objectives, objIds);
  const horizonLabel = labels(horizons, [horizon])[0] || "";
  const involvementLabel = labels(involvements, [involvement])[0] || "";
  const update = (k, val) => setForm((f) => ({ ...f, [k]: val }));
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const started = useRef(false);
  useEffect(() => { if (!started.current) { started.current = true; track("buyer_brief_start", { lead_type: "investor", cta_location: "investor_funnel" }); } }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!budget) { toast.error(v("budgetRequired")); scrollTo("invest-step-budget"); return; }
    if (!objIds.length) { toast.error(v("objectiveRequired")); scrollTo("invest-step-objective"); return; }
    setLoading(true);
    const message = [
      "INVESTOR BRIEF",
      `Comparing: ${destLabels.join(", ") || "-"}`,
      `Budget: ${budgetLabel}`,
      `Objectives: ${objLabels.join("; ")}`,
      `Horizon: ${horizonLabel || "-"}`,
      `Involvement: ${involvementLabel || "-"}`,
      `Tax residence: ${form.country.trim() || "-"}`,
      form.note.trim() ? `Note: ${form.note.trim()}` : null,
    ].filter(Boolean).join("\n");
    try {
      await submitLead({
        name: form.name, email: form.email, phone: form.phone, interest: "buy", message, source: "investor-brief", lang,
        meta: { destinations: destIds, budget, objectives: objIds, horizon, involvement, tax_residence: form.country.trim() || null },
      });
      track("buyer_brief_complete", { lead_type: "investor", cta_location: "investor_funnel" });
      trackLead("investor", "investor_funnel");
      navigate(`/${lang}/invest/thank-you`, { state: { name: form.name } });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };


  return (
    <div data-testid="invest-page">
      <Seo title={v("seoTitle")} description={v("seoDesc")} />

      <section className="relative w-full min-h-[60vh] flex items-end vl-grain overflow-hidden">
        <img src={optimizeImg(HERO, 1920)} alt="French Riviera coastline" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/75 to-background/30" />
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] w-full pt-40 pb-16 md:pb-24">
          <Reveal>
            <p className={overline}>{v("overline")}</p>
            <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-3xl whitespace-pre-line">{v("title")}</h1>
            <p className="mt-8 font-body font-light text-lg text-foreground/80 max-w-2xl leading-relaxed">{v("intro")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <form onSubmit={handleSubmit} data-testid="invest-form" className="lg:col-span-7 space-y-20">
            <Reveal>
              <div data-testid="invest-step-destinations">
                <StepHeading n={1} title={v("destTitle")} hint={v("destHint")} />
                <ChipGroup options={dests} value={destIds} onChange={setDestIds} multi testPrefix="dest" label={v("destTitle")} />
              </div>
            </Reveal>
            <Reveal>
              <div id="invest-step-budget" data-testid="invest-step-budget" className="scroll-mt-32">
                <StepHeading n={2} title={v("budgetTitle")} hint={v("budgetHint")} />
                <ChipGroup options={budgets} value={budget} onChange={setBudget} testPrefix="budget" label={v("budgetTitle")} />
              </div>
            </Reveal>
            <Reveal>
              <div id="invest-step-objective" data-testid="invest-step-objective" className="scroll-mt-32">
                <StepHeading n={3} title={v("objectiveTitle")} hint={v("objectiveHint")} />
                <ChipGroup options={objectives} value={objIds} onChange={setObjIds} multi testPrefix="objective" label={v("objectiveTitle")} />
              </div>
            </Reveal>
            <Reveal>
              <div data-testid="invest-step-horizon">
                <StepHeading n={4} title={v("horizonTitle")} hint={v("horizonHint")} />
                <ChipGroup options={horizons} value={horizon} onChange={setHorizon} testPrefix="horizon" label={v("horizonTitle")} />
                <p className={`${labelClass} mt-10 mb-3`}>{v("involvementTitle")}</p>
                <ChipGroup options={involvements} value={involvement} onChange={setInvolvement} testPrefix="involvement" label={v("involvementTitle")} />
              </div>
            </Reveal>
            <Reveal>
              <div data-testid="invest-step-details">
                <StepHeading n={5} title={v("formTitle")} hint={v("formIntro")} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="invest-name" className={labelClass}>{t("contact.name")}</label>
                    <input id="invest-name" data-testid="invest-name" name="name" autoComplete="name" required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="invest-email" className={labelClass}>{t("contact.email")}</label>
                    <input id="invest-email" data-testid="invest-email" name="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="invest-phone" className={labelClass}>{t("contact.phone")}</label>
                    <input id="invest-phone" data-testid="invest-phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="invest-country" className={labelClass}>{v("countryLabel")}</label>
                    <input id="invest-country" data-testid="invest-country" name="country" autoComplete="country-name" placeholder={v("countryPlaceholder")} value={form.country} onChange={(e) => update("country", e.target.value)} className={inputClass} />
                    <p className="font-body text-xs text-muted-foreground/80 leading-relaxed">{v("countryHelp")}</p>
                  </div>
                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <label htmlFor="invest-note" className={labelClass}>{v("note")}</label>
                    <textarea id="invest-note" data-testid="invest-note" name="note" rows={4} value={form.note} onChange={(e) => update("note", e.target.value)} placeholder={v("notePlaceholder")} className={`${inputClass} resize-none`} />
                  </div>
                </div>
                <button type="submit" data-testid="invest-submit" disabled={loading} className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
                  {loading ? <><Loader2 size={15} className="animate-spin" /> {t("contact.sending")}</> : <>{v("submit")} <ArrowRight size={15} /></>}
                </button>
                <p className="mt-4 font-body text-xs text-muted-foreground/70">{v("privacy")}</p>
              </div>
            </Reveal>
          </form>

          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <Reveal delay={0.1}>
                <div data-testid="invest-brief" className="border border-primary/30 bg-secondary p-8">
                  <p className={overline}>{v("summaryTitle")}</p>
                  <dl className="mt-6 space-y-4">
                    <BriefRow label={L.destinations} value={destLabels.join(" · ") || L.none} testId="brief-destinations" />
                    <BriefRow label={L.budget} value={budgetLabel || L.none} testId="brief-budget" />
                    <BriefRow label={L.objectives} value={objLabels.join(" · ") || L.none} testId="brief-objectives" />
                    <BriefRow label={L.horizon} value={[horizonLabel, involvementLabel].filter(Boolean).join(" · ") || L.none} testId="brief-horizon" />
                  </dl>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div data-testid="nice-glance" className="border border-border/60 bg-background/40 p-8">
                  <p className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.25em] text-primary"><BarChart3 size={14} /> {G.title}</p>
                  <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4">
                    {GLANCE.map((g) => (
                      <div key={g.k} className="border-l-2 border-primary/40 pl-3">
                        <dd className="font-headings text-2xl text-foreground leading-none">{g.v}</dd>
                        <dt className="mt-1 font-accent text-[9px] uppercase tracking-[0.2em] text-foreground/55 leading-snug">{G.items[g.k]}</dt>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-5 font-body text-xs text-muted-foreground/80 leading-relaxed">{G.note}</p>
                  <Link to="/riviera-real-estate-data-2026" data-testid="glance-report-link" className="mt-3 inline-flex items-center gap-1.5 font-accent text-[11px] uppercase tracking-[0.2em] text-primary hover:text-primary/80 transition-colors">
                    {G.link} <ArrowRight size={12} />
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="space-y-3">
                  {v("trust").map((line) => (
                    <li key={line} className="flex items-start gap-3 font-body font-light text-sm text-foreground/70">
                      <ShieldCheck size={15} className="text-primary shrink-0 mt-0.5" /> {line}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
